import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getInventoryValueByProject } from '../../services/api/implementation/impl';
import { formatINR } from '../../utils/inventoryValue';
import { inrLakhCrore } from '../../utils/inrLakhCrore';

const Num = ({ children, className = '' }) => (
  <td className={`whitespace-nowrap px-3 py-2 text-right tabular-nums ${className}`}>{children}</td>
);

/** Amount with its lakh/crore reading on hover. */
const Money = ({ value }) => (
  <span title={Math.abs(Number(value)) >= 100000 ? inrLakhCrore(value) : undefined}>
    {formatINR(value)}
  </span>
);

/**
 * W4.4: stock value, own stock value and this month's material cost for each
 * project the user can see. `projectId` narrows it to one project.
 */
const InventoryValueByProject = ({ projectId = null }) => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['inventoryValueByProject'],
    queryFn: () => getInventoryValueByProject(),
    retry: false,
    staleTime: 5 * 60 * 1000
  });

  // Older servers have no report route; the dashboard stays as it was.
  if (isError) return null;

  const all = data?.projects || [];
  const one = projectId !== null && projectId !== undefined && String(projectId) !== '';
  const rows = one ? all.filter((p) => String(p.project_id) === String(projectId)) : all;
  const totals = one
    ? {
        stock_value: rows[0]?.stock_value,
        own_stock_value: rows[0]?.own_stock_value,
        month_cost: rows[0]?.month_cost
      }
    : data?.totals || {};
  const unpriced = rows.reduce((s, p) => s + (Number(p.unpriced_items) || 0), 0);

  return (
    <div className="w-full rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3">
        <h3 className="text-xl font-semibold text-[#1E4A53]">Inventory value</h3>
        <p className="mt-0.5 text-sm text-gray-500">
          Stock on hand and material issued to site this month, by project
        </p>
      </div>

      <div className="mb-3 grid grid-cols-1 gap-3 md:grid-cols-3">
        {[
          { label: 'Stock value', value: totals.stock_value },
          { label: 'Own stock value', value: totals.own_stock_value },
          { label: "This month's material cost", value: totals.month_cost }
        ].map((card) => (
          <div key={card.label} className="rounded-2xl border border-gray-200 bg-[#eef4fa] p-3">
            <p className="text-xs font-semibold text-gray-700">{card.label}</p>
            <p className="mt-3 text-2xl font-semibold leading-none text-[#0d223a]">
              {isLoading ? '…' : formatINR(card.value)}
            </p>
            {Math.abs(Number(card.value)) >= 100000 && (
              <p className="mt-1 text-xs font-medium text-gray-500">{inrLakhCrore(card.value)}</p>
            )}
          </div>
        ))}
      </div>

      {!one && rows.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-gray-100">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-[#F4F7F8] text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">Project</th>
                <th className="px-3 py-2 text-right">Stock value</th>
                <th className="px-3 py-2 text-right">Own value</th>
                <th className="px-3 py-2 text-right">Cost this month</th>
                <th className="px-3 py-2 text-right">Open lends</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr
                  key={p.project_id}
                  className="cursor-pointer border-t border-gray-100 hover:bg-gray-50"
                  onClick={() =>
                    navigate('/builder/inventory/dashboard', {
                      state: { projectID: p.project_id }
                    })
                  }>
                  <td className="px-3 py-2 text-gray-900">
                    {p.project_name || `Project #${p.project_id}`}
                    {Number(p.low_stock) > 0 && (
                      <span className="ml-2 text-xs text-amber-700">{p.low_stock} low</span>
                    )}
                  </td>
                  <Num>
                    <Money value={p.stock_value} />
                  </Num>
                  <Num>
                    <Money value={p.own_stock_value} />
                  </Num>
                  <Num>
                    <Money value={p.month_cost} />
                  </Num>
                  <Num className="text-gray-700">
                    {Number(p.open_lends?.lent_out) || 0} out · {Number(p.open_lends?.borrowed) || 0} in
                    {Number(p.open_lends?.overdue) > 0 && (
                      <span className="ml-1 text-red-600">({p.open_lends.overdue} overdue)</span>
                    )}
                  </Num>
                </tr>
              ))}
            </tbody>
            {rows.length > 1 && (
              <tfoot className="border-t-2 border-gray-200 bg-[#F9FAFB] font-semibold">
                <tr>
                  <td className="px-3 py-2">Total</td>
                  <Num>
                    <Money value={totals.stock_value} />
                  </Num>
                  <Num>
                    <Money value={totals.own_stock_value} />
                  </Num>
                  <Num>
                    <Money value={totals.month_cost} />
                  </Num>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
      {!isLoading && rows.length === 0 && (
        <p className="text-sm text-gray-400">No inventory value to show yet.</p>
      )}
      {unpriced > 0 && (
        <p className="mt-2 text-xs text-gray-500">
          {unpriced} item{unpriced === 1 ? '' : 's'} in stock without a rate, counted at ₹0.
        </p>
      )}
    </div>
  );
};

export default InventoryValueByProject;
